import { useState, useRef, useLayoutEffect, useCallback, type RefObject } from 'react';

export interface ColumnResizeStart {
  pointerId?: number;
}

interface UseResizableColumnOptions {
  /** Initial width in pixels */
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  /**
   * Live upper bound from the surrounding row. The rendered width follows it
   * immediately. A wider stored preference is kept until the user drags.
   */
  availableWidth?: number;
  storageKey?: string;
  /**
   * When set, width is measured from the container edge:
   * - `start` (default): `clientX - containerRect.left` (left column / sidebar)
   * - `end`: `containerRect.right - clientX` (right column)
   */
  containerRef?: RefObject<HTMLElement | null>;
  /** Which container edge the column is anchored to. Default `start`. */
  edge?: 'start' | 'end';
  /**
   * Attach to the column element whose width is being resized.
   * While dragging, width is written to the DOM directly and React state is
   * only committed on pointerup — avoids re-rendering the whole page every pixel.
   */
  columnRef?: RefObject<HTMLElement | null>;
}

interface UseResizableColumnReturn {
  /** Width to render, after the live cap. */
  width: number;
  /** User preference before the live cap. */
  preferredWidth: number;
  resizing: boolean;
  startResize: (event?: ColumnResizeStart) => void;
  resetWidth: () => void;
  setWidth: (width: number) => void;
}

function persistWidth(storageKey: string | undefined, width: number) {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    localStorage.setItem(storageKey, String(width));
  } catch {
    /* ignore */
  }
}

function readStoredWidth(storageKey: string | undefined, minWidth: number, maxWidth: number): number | null {
  if (!storageKey || typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed >= minWidth && parsed <= maxWidth) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

export function useResizableColumn(options: UseResizableColumnOptions = {}): UseResizableColumnReturn {
  const {
    defaultWidth = 368,
    minWidth = 260,
    maxWidth = 680,
    availableWidth,
    storageKey,
    containerRef,
    edge = 'start',
    columnRef,
  } = options;

  const [preferredWidth, setPreferredWidth] = useState<number>(
    () => readStoredWidth(storageKey, minWidth, maxWidth) ?? defaultWidth,
  );
  const preferredRef = useRef(preferredWidth);
  const [width, setWidthState] = useState(preferredWidth);
  const [resizing, setResizing] = useState(false);
  const isResizingRef = useRef(false);
  const activePointerRef = useRef<number | null>(null);
  const liveWidthRef = useRef(width);
  const rafRef = useRef<number | null>(null);
  const pendingWidthRef = useRef<number | null>(null);
  const bodyStyleRef = useRef<{ cursor: string; userSelect: string } | null>(null);

  const effectiveMax = availableWidth == null ? maxWidth : Math.min(maxWidth, availableWidth);

  const clampTo = useCallback(
    (w: number, max: number) => {
      if (max < minWidth) return minWidth;
      return Math.max(minWidth, Math.min(Math.round(w), max));
    },
    [minWidth],
  );

  const clampWidth = useCallback(
    (w: number) => clampTo(w, effectiveMax),
    [clampTo, effectiveMax],
  );

  const writeColumnWidth = useCallback(
    (next: number) => {
      const el = columnRef?.current;
      if (el) el.style.width = `${next}px`;
    },
    [columnRef],
  );

  const clampedPreferred = clampTo(preferredRef.current, effectiveMax);
  if (!isResizingRef.current && width !== clampedPreferred) {
    setWidthState(clampedPreferred);
    liveWidthRef.current = clampedPreferred;
  }

  useLayoutEffect(() => {
    if (isResizingRef.current) return;
    writeColumnWidth(width);
  }, [width, writeColumnWidth]);

  const applyLiveWidth = useCallback(
    (next: number) => {
      if (next === liveWidthRef.current) {
        writeColumnWidth(next);
        return;
      }
      liveWidthRef.current = next;
      if (columnRef?.current) {
        writeColumnWidth(next);
        return;
      }
      pendingWidthRef.current = next;
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const pending = pendingWidthRef.current;
        pendingWidthRef.current = null;
        if (pending == null) return;
        setWidthState((prev) => (prev === pending ? prev : pending));
      });
    },
    [columnRef, writeColumnWidth],
  );

  const commitPreferred = useCallback(
    (next: number) => {
      const clamped = clampWidth(next);
      preferredRef.current = clamped;
      liveWidthRef.current = clamped;
      writeColumnWidth(clamped);
      setPreferredWidth(clamped);
      setWidthState(clamped);
      persistWidth(storageKey, clamped);
    },
    [clampWidth, storageKey, writeColumnWidth],
  );

  const setWidth = useCallback(
    (newWidth: number) => {
      commitPreferred(newWidth);
    },
    [commitPreferred],
  );

  const restoreBodyStyle = useCallback(() => {
    const previous = bodyStyleRef.current;
    if (!previous) return;
    document.body.style.cursor = previous.cursor;
    document.body.style.userSelect = previous.userSelect;
    bodyStyleRef.current = null;
  }, []);

  const endResize = useCallback(() => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    activePointerRef.current = null;
    setResizing(false);
    restoreBodyStyle();
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pendingWidthRef.current = null;
    const live = liveWidthRef.current;
    preferredRef.current = live;
    writeColumnWidth(live);
    setPreferredWidth(live);
    setWidthState((prev) => (prev === live ? prev : live));
    persistWidth(storageKey, live);
  }, [restoreBodyStyle, storageKey, writeColumnWidth]);

  const startResize = useCallback((event?: ColumnResizeStart) => {
    isResizingRef.current = true;
    activePointerRef.current = event?.pointerId ?? null;
    setResizing(true);
    if (!bodyStyleRef.current) {
      bodyStyleRef.current = {
        cursor: document.body.style.cursor,
        userSelect: document.body.style.userSelect,
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
  }, []);

  const resetWidth = useCallback(() => {
    setWidth(defaultWidth);
  }, [defaultWidth, setWidth]);

  useLayoutEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (!isResizingRef.current) return;
      if (activePointerRef.current != null && e.pointerId !== activePointerRef.current) return;
      if (e.cancelable) e.preventDefault();
      let next: number;
      if (containerRef?.current) {
        const r = containerRef.current.getBoundingClientRect();
        next = edge === 'end' ? r.right - e.clientX : e.clientX - r.left;
      } else {
        next = edge === 'end' ? window.innerWidth - e.clientX : e.clientX;
      }
      applyLiveWidth(clampWidth(next));
    };

    const handlePointerEnd = (e: PointerEvent) => {
      if (!isResizingRef.current) return;
      if (activePointerRef.current != null && e.pointerId !== activePointerRef.current) return;
      endResize();
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: false });
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [applyLiveWidth, clampWidth, containerRef, edge, endResize]);

  return { width, preferredWidth, resizing, startResize, resetWidth, setWidth };
}
